import React, { useState, useEffect } from 'react';
import {
  Box, VStack, Text, Flex, Icon, Divider, Button, Image, IconButton,
  useColorModeValue, Badge, Avatar, HStack, Drawer, 
  DrawerOverlay, DrawerContent, DrawerCloseButton, DrawerBody,
  useBreakpointValue, useDisclosure, Heading, Menu, MenuButton, MenuList,
  MenuItem, MenuDivider, Modal, ModalOverlay, ModalContent, ModalHeader,
  ModalFooter, ModalBody, ModalCloseButton, AvatarBadge
} from '@chakra-ui/react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useUser } from '../Context/UserContext';
import {
  MdLogout, MdHelp
} from 'react-icons/md';
import { 
  FaBars
} from 'react-icons/fa';
import { 
  FiUser, FiSettings, FiLogOut, FiSliders, FiBarChart2,
} from 'react-icons/fi';
import logo from '../../Images/logo_tovi.png';

// Import modular components
import { SidebarItem } from './SidebarComponents';
import {
  FinancesMenu,
  WorkersMenu,
  ClientsMenu,
  AdminUsersMenu,
  MobileConfigMenu,
  TermsMenu,
  TestToolsMenu
} from './MenuItems';

// Main Sidebar Component
const Sidebar = () => {
  const { user, logoutUser } = useUser();
  const navigate = useNavigate();
  const location = useLocation();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [confirmLogout, setConfirmLogout] = useState(false);
  
  // Responsive behavior
  const isMobile = useBreakpointValue({ base: true, lg: false });
  const sidebarWidth = useBreakpointValue({ base: "full", md: "280px" });
  
  // Theme colors
  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const headerBgColor = useColorModeValue('#4bedffff', '#4bedffff');
  const headerTextColor = useColorModeValue('black', 'black');
  const scrollbarThumbColor = useColorModeValue('gray.300', 'gray.600');
  const scrollbarTrackColor = useColorModeValue('gray.100', 'gray.700');
  
  // Helper function to check if user has any of the specified roles
  const hasRole = (roles) => {
    if (!user || !user.permissions) return false;
    if (user.permissions.includes('Master')) return true; // Master role has access to everything
    return roles.some(role => user.permissions.includes(role));
  };

  // Check for specific role access
  const canAccessFinanceiro = hasRole(['Master', 'Administração', 'Financeiro']);
  const canAccessContratados = hasRole(['Master', 'Suporte']);
  const canAccessContratantes = hasRole(['Master', 'Suporte']);
  const canAccessUsuariosAdmin = hasRole(['Master', 'TI']);
  const canAccessMobileConfig = hasRole(['Master', 'Marketing', 'Design']);
  const canAccessTermos = hasRole(['Master']);
  const canAccessTestTools = hasRole(['Master']);
  
  // Check if current route is active
  const isActive = (path) => {
    return location.pathname.startsWith(path);
  };
  
  // Handle sidebar navigation and mobile drawer
  useEffect(() => {
    // Close mobile drawer on route change
    if (isMobile && isOpen) {
      onClose();
    }
  }, [location.pathname, isMobile, isOpen, onClose]);

  // Sidebar content (JSX variable, not a component — avoids remounting children on re-render)
  const sidebarContent = (
    <Box
      bg={bgColor}
      w="full"
      h="full"
      position="relative"
      display="flex"
      flexDirection="column"
      css={{
        '&::-webkit-scrollbar': {
          width: '6px',
        },
        '&::-webkit-scrollbar-track': {
          width: '8px',
          background: scrollbarTrackColor,
        },
        '&::-webkit-scrollbar-thumb': {
          background: scrollbarThumbColor,
          borderRadius: '24px',
        },
      }}
    >
      {/* Logo and header section - This stays fixed at top */}
      <Flex 
        direction="column" 
        align="center" 
        justify="space-between" 
        py="5" 
        px="4" 
        bg={headerBgColor}
        color={headerTextColor}
        borderBottomWidth="1px"
        borderColor={borderColor}
        flexShrink={0}
      >
        <Flex align="center" mb="4" width="100%" justifyContent="center">
          <Image 
            src={logo}
            alt="Logo"
            boxSize="50px"
            borderRadius="full"
            bg="white"
            p="2"
            mr="3"
            transition="transform 0.3s ease"
            _hover={{ transform: 'scale(1.05)' }}
          />
          <VStack spacing="0" align="start">
            <Heading as="h1" size="md" fontWeight="bold">TOVI App</Heading>
            <Text fontSize="xs" opacity="0.8">Painel Administrativo</Text>
          </VStack>
        </Flex>
        
        {user && (
          <HStack 
            spacing="3" 
            width="full" 
            bg="blackAlpha.200" 
            p="2" 
            borderRadius="md"
            mt="2"
          >
            <Avatar 
              size="sm" 
              name={user.username || 'User'} 
              bg="teal.300"
              color="white"
            >
              {user.isOnline && <AvatarBadge boxSize='1.25em' bg='green.500' />}
            </Avatar>
            
            <VStack spacing="0" align="start" flex="1">
              <Text fontSize="sm" fontWeight="bold" isTruncated maxW="150px">
                {user.username || 'Usuário'}
              </Text>
              <Badge 
                colorScheme="teal" 
                variant="solid" 
                fontSize="2xs"
                px="1"
              >
                {user.permissions && user.permissions[0]}
              </Badge>
            </VStack>
            
            <Menu>
              <MenuButton
                as={IconButton}
                size="sm"
                variant="ghost"
                color="black"
                icon={<FiSettings />}
                _hover={{ bg: 'rgba(0,0,0,0.1)' }}
              />
              <MenuList textColor="black">
                <MenuItem icon={<FiUser />} onClick={() => navigate('/perfil')}>
                  Meu Perfil
                </MenuItem>
                <MenuItem icon={<FiSliders />} onClick={() => navigate('/preferencias')}>
                  Preferências
                </MenuItem>
                <MenuDivider />
                <MenuItem 
                  icon={<FiLogOut />} 
                  color="red.500"
                  onClick={() => setConfirmLogout(true)}
                >
                  Sair
                </MenuItem>
              </MenuList>
            </Menu>
          </HStack>
        )}
      </Flex>

      {/* Menu items - This section scrolls */}
      <Box
        flex="1"
        overflowY="auto"
        css={{
          '&::-webkit-scrollbar': {
            width: '6px',
          },
          '&::-webkit-scrollbar-track': {
            width: '8px',
            background: scrollbarTrackColor,
          },
          '&::-webkit-scrollbar-thumb': {
            background: scrollbarThumbColor,
            borderRadius: '24px',
          },
        }}
      >
        <VStack align="stretch" spacing="1" pt="4" pb="6">
        <Text 
          px="6" 
          mb="2" 
          color="gray.500" 
          fontSize="xs" 
          fontWeight="semibold" 
          textTransform="uppercase"
          letterSpacing="wider"
        >
          Menu Principal
        </Text>
        
        {/* Dashboard */}
        <SidebarItem 
          icon={FiBarChart2} 
          label="Dashboard" 
          to="/main" 
          active={isActive('/main')} 
        />

        {/* Finances Section */}
        {canAccessFinanceiro && <FinancesMenu />}
        
        {/* Workers Section */}
        {canAccessContratados && <WorkersMenu/>}

        {/* Clients Section */}
        {canAccessContratantes && <ClientsMenu />}
        
        {/* Admin Users Section */}
        {canAccessUsuariosAdmin && <AdminUsersMenu />}
        
        {/* Mobile App Config Section */}
        {canAccessMobileConfig && <MobileConfigMenu />}

        {/* Terms and Conditions Section */}
        {canAccessTermos && <TermsMenu />}

        {/* Test Tools Section - Master Only */}
        {canAccessTestTools && <TestToolsMenu />}

        <Divider my="6" borderColor={borderColor} />
        
        {/* Help and Support Section */}
        <Text
          px="6"
          mb="2"
          color="gray.500"
          fontSize="xs"
          fontWeight="semibold"
          textTransform="uppercase"
          letterSpacing="wider"
        >
          Suporte
        </Text>
        
        <SidebarItem 
          icon={MdHelp} 
          label="Ajuda e FAQ" 
          to="/ajuda" 
          active={isActive('/ajuda')} 
        />
        </VStack>
      </Box>

      {/* Logout button at bottom */}
      {user && (
        <Box 
          p="5" 
          width="100%" 
          bg={bgColor} 
          borderTop="1px" 
          borderColor={borderColor}
          boxShadow="0 -2px 10px rgba(0,0,0,0.05)"
          flexShrink={0}
        >
          <Button 
            colorScheme="yellow" 
            bg="#4bedffff"
            color="black"
            variant="solid" 
            onClick={() => setConfirmLogout(true)} 
            width="full"
            leftIcon={<Icon as={MdLogout} />}
            _hover={{ bg: '#E6C200', transform: 'translateY(-2px)' }}
            transition="all 0.3s ease"
          >
            Sair
          </Button>
        </Box>
      )}
    </Box>
  );

  return (
    <>
      {/* Mobile hamburger button */}
      {isMobile && (
        <IconButton
          aria-label="Open menu"
          icon={<FaBars />}
          position="fixed"
          top="4"
          left="4"
          zIndex="1000"
          colorScheme="teal"
          onClick={onOpen}
          size="md"
          display={{ base: 'flex', lg: 'none' }}
        />
      )}
      
      {/* Mobile Drawer */}
      <Drawer
        isOpen={isOpen}
        placement="left"
        onClose={onClose}
        returnFocusOnClose={false}
        size={sidebarWidth}
      >
        <DrawerOverlay />
        <DrawerContent display="flex" flexDirection="column">
          <DrawerCloseButton color="white" zIndex="1000" />
          <DrawerBody p="0" overflow="hidden" display="flex" flexDirection="column">
            {isOpen && sidebarContent}
          </DrawerBody>
        </DrawerContent>
      </Drawer>
      
      {/* Desktop Sidebar */}
      <Box
        display={{ base: 'none', lg: 'block' }}
        position="fixed"
        h="100vh"
        w={sidebarWidth}
        borderRight="1px"
        borderColor={borderColor}
        boxShadow="lg"
        zIndex="900"
        overflow="hidden"
      >
        {sidebarContent}
      </Box>
      
      {/* Confirm Logout Modal */}
      <Modal isOpen={confirmLogout} onClose={() => setConfirmLogout(false)}>
        <ModalOverlay backdropFilter="blur(2px)" />
        <ModalContent>
          <ModalHeader>Confirmar Saída</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            Tem certeza que deseja sair do sistema?
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={() => setConfirmLogout(false)}>
              Cancelar
            </Button>
            <Button 
              colorScheme="red" 
              leftIcon={<FiLogOut />}
              onClick={() => {
                logoutUser();
                setConfirmLogout(false);
              }}
            >
              Sair
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

export default Sidebar;